import { findPriceByName } from './ingredientMatch';
import { usableG } from './priceYield';   // V152 실사용량 (신선식품 로스 제외)
// ── menuProfitability.js — 메뉴 이익률 랭킹 ─────────────────────
// 각 메뉴 (type === 'menu')의 1인분 원가/판매가/마진/마진율 계산
// 정렬: 마진율 내림차순 / 절대이익 내림차순 / 판매가 오름차순
//
// 입력:
//   categories[] — { items: [ { type, name, selling_price, ingredients[], total_output, serving_g, ... } ] }
//   prices[] — { name, price, net_g, code, ... }
//   priceAverages — Map<code, {avg, count}>  (선택, 없으면 등록가 사용)
// 출력:
//   menus[] — { name, sellingPrice, costPerUnit, profit, marginPct, costRatio, ingredientCount, missingIngredients[] }

function calcMenuCost(menu, prices, priceAverages) {
  const ings = menu.ingredients || [];
  if (ings.length === 0) return { totalCost: 0, missing: [] };

  let totalCost = 0;
  const missing = [];
  for (const ing of ings) {
    const price = findPriceByName(prices, ing.name);
    if (!price || !usableG(price)) {   // V152 실사용량
      missing.push(ing.name);
      continue;
    }
    let effPrice = price.price;
    if (priceAverages) {
      const stat = priceAverages.get(price.code);
      if (stat && stat.count > 0 && stat.avg > 0) effPrice = stat.avg;
    }
    const qty = Number(ing.qty) || 0;
    if (qty <= 0) continue;
    totalCost += (qty / usableG(price)) * effPrice;
  }
  return { totalCost, missing };
}

export function calcMenuProfitability({
  categories = [],
  prices = [],
  priceAverages = null,
}) {
  if (!Array.isArray(categories) || !Array.isArray(prices)) return [];

  const result = [];
  for (const cat of categories) {
    for (const item of (cat.items || [])) {
      if (item.type !== 'menu') continue;
      const sellingPrice = Number(item.selling_price) || 0;
      const { totalCost, missing } = calcMenuCost(item, prices, priceAverages);
      const totalOutput = Number(item.total_output) || 0;
      const servingG = Number(item.serving_g) || 0;
      const costPerUnit = (totalOutput > 0 && servingG > 0)
        ? (totalCost / totalOutput) * servingG
        : totalCost;
      const profit = sellingPrice > 0 ? sellingPrice - costPerUnit : null;
      const marginPct = sellingPrice > 0 ? ((sellingPrice - costPerUnit) / sellingPrice) * 100 : null;
      const costRatio = sellingPrice > 0 ? (costPerUnit / sellingPrice) * 100 : null;
      const ingredientCount = (item.ingredients || []).length;
      const missingCount = missing.length;
      const matchedCount = ingredientCount - missingCount;
      const dataQuality = ingredientCount > 0 ? matchedCount / ingredientCount : 0;

      result.push({
        id: item.id || item.name,
        name: item.name,
        category: cat.name || '',
        sellingPrice,
        costPerUnit: Math.round(costPerUnit),
        profit: profit !== null ? Math.round(profit) : null,
        marginPct: marginPct !== null ? Math.round(marginPct * 10) / 10 : null,
        costRatio: costRatio !== null ? Math.round(costRatio * 10) / 10 : null,
        ingredientCount,
        missingIngredients: missing,
        dataQuality,  // 0~1 (등록된 단가 비율)
        hasPrice: sellingPrice > 0,
        hasIngredients: ingredientCount > 0 && missingCount < ingredientCount,
      });
    }
  }
  return result;
}

// ── 정렬 + 필터 헬퍼 ──
export function rankMenusByMargin(menus) {
  const evaluable = menus.filter(m => m.hasPrice && m.hasIngredients && m.marginPct !== null);
  return [...evaluable].sort((a, b) => b.marginPct - a.marginPct);
}

export function rankMenusByProfit(menus) {
  const evaluable = menus.filter(m => m.hasPrice && m.hasIngredients && m.profit !== null);
  return [...evaluable].sort((a, b) => b.profit - a.profit);
}

// ── 메뉴명 정규화 (fuzzy 매칭용) ──
export function normalizeMenuName(name) {
  if (!name) return '';
  return String(name)
    .toLowerCase()
    .replace(/[\s_\-(){}\[\]·,./]/g, '')
    .trim();
}

const STRIP_TOKENS = ['일반', '보통', '특선', '스페셜', '추천', '신메뉴', '시그니처'];

function deepNormalize(name) {
  let s = normalizeMenuName(name);
  for (const t of STRIP_TOKENS) {
    s = s.replace(new RegExp(t, 'g'), '');
  }
  return s;
}

export function isMenuMatch(a, b) {
  if (!a || !b) return false;
  const na = normalizeMenuName(a);
  const nb = normalizeMenuName(b);
  if (na === nb) return true;
  if (na.length >= 2 && nb.length >= 2 && (na.includes(nb) || nb.includes(na))) return true;
  const da = deepNormalize(a);
  const db = deepNormalize(b);
  if (da === db && da.length >= 2) return true;
  return false;
}

// ── 실제 판매 데이터 집계 (transactions의 lineItems에서) ──
export function aggregateMenuSales(transactions, dateRange = null) {
  const agg = {};
  if (!Array.isArray(transactions)) return agg;
  for (const t of transactions) {
    if (t?.type !== 'income') continue;
    if (!Array.isArray(t.lineItems) || t.lineItems.length === 0) continue;
    if (dateRange && t.date) {
      if (dateRange.start && t.date < dateRange.start) continue;
      if (dateRange.end && t.date > dateRange.end) continue;
    }
    for (const li of t.lineItems) {
      const rawName = li.menuId || li.name;
      if (!rawName) continue;
      const qty = Number(li.qty) || Number(li.quantity) || 0;
      if (qty <= 0) continue;
      const revenue =
        Number(li.total) ||
        Number(li.amount) ||
        (qty * (Number(li.unitPrice) || 0));
      const key = normalizeMenuName(rawName) || rawName;
      if (!agg[key]) {
        agg[key] = { name: rawName, qty: 0, revenue: 0, rawNames: new Set() };
      }
      agg[key].qty += qty;
      agg[key].revenue += revenue;
      agg[key].rawNames.add(rawName);
    }
  }
  return agg;
}

// ── 메뉴별 실제 이익 계산 (fuzzy 매칭) ──
export function enrichWithActualSales(menus, salesAgg) {
  if (!salesAgg || Object.keys(salesAgg).length === 0) return menus;
  return menus.map(m => {
    let sale = null;
    if (m.id && salesAgg[m.id]) sale = salesAgg[m.id];
    if (!sale && m.name) {
      const key = normalizeMenuName(m.name);
      sale = salesAgg[key];
    }
    if (!sale && m.name) {
      for (const aggKey of Object.keys(salesAgg)) {
        if (isMenuMatch(m.name, salesAgg[aggKey].name)) {
          sale = salesAgg[aggKey];
          break;
        }
      }
    }
    if (!sale || sale.qty <= 0) return { ...m, actualSales: null };
    const actualProfit = (m.profit !== null && m.profit !== undefined)
      ? Math.round(m.profit * sale.qty)
      : null;
    return {
      ...m,
      actualSales: {
        qty: sale.qty,
        revenue: Math.round(sale.revenue),
        actualProfit,
      },
    };
  });
}

// ── POS에서 발견된 메뉴 중 셈핏에 등록 안 된 항목 ──
export function findUnregisteredMenus(salesAgg, registeredMenus) {
  if (!salesAgg) return [];
  const out = [];
  for (const key of Object.keys(salesAgg)) {
    const sale = salesAgg[key];
    if (!sale || sale.qty <= 0) continue;
    const matched = (registeredMenus || []).some(m => isMenuMatch(m.name, sale.name));
    if (!matched) {
      out.push({
        name: sale.name,
        rawNames: Array.from(sale.rawNames || [sale.name]),
        qty: sale.qty,
        revenue: sale.revenue,
        avgPrice: sale.qty > 0 ? Math.round(sale.revenue / sale.qty) : 0,
      });
    }
  }
  return out.sort((a, b) => b.qty - a.qty);
}

// ── 요약 통계 ──
export function summarizeMenuProfitability(menus) {
  const evaluable = menus.filter(m => m.hasPrice && m.hasIngredients && m.marginPct !== null);
  if (evaluable.length === 0) {
    return { count: 0, avgMargin: null, top: null, bottom: null, lossCount: 0 };
  }
  const avgMargin = evaluable.reduce((s, m) => s + m.marginPct, 0) / evaluable.length;
  const sorted = [...evaluable].sort((a, b) => b.marginPct - a.marginPct);
  const lossCount = evaluable.filter(m => m.marginPct < 0).length;
  return {
    count: evaluable.length,
    totalMenus: menus.length,
    incompleteCount: menus.length - evaluable.length,
    avgMargin: Math.round(avgMargin * 10) / 10,
    top: sorted[0],
    bottom: sorted[sorted.length - 1],
    lossCount,
  };
}
